import {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {InformationLineIcon} from '~library/svg/InformationLineIcon';
import {Button} from '~library/atoms/Button';

import './Tooltip.sass';

export const Tooltip = ({
  children,
  text,
  className,
  position = 'bottom',
  color = 'cotton_candy',
  visible = false,
  expandedImgSrc = null,
  dataQaId,
  hasCloseButton = true,
  shimmer = false,
  showOnce = false,
  shrink = false,
  id = '',
  centerText = false,
  mobileTextSize = false,
  maxContent = false,
}) => {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const expandedImgRef = useRef();

  const onClickCloseHandler = useCallback(() => {
    setShow(false);

    // Store the id we want to hide (event specific), then we can hide it later once we have an event id
    if (showOnce) localStorage.setItem('tooltip-id-to-hide', id.split('_')[0]);
  }, []);

  useEffect(() => {
    setShow(visible);
  }, [visible]);

  const classNames = cx('create-flow_tooltip-container', {
    [`create-flow_tooltip-container__${position}`]: position,
    [`create-flow_tooltip-container__${className}`]: className,
    [`create-flow_tooltip-container__${color}`]: color,
    'create-flow_tooltip-container__shimmer': shimmer && !expanded,
    'create-flow_tooltip-container__expanded': expanded,
    'create-flow_tooltip-container__shrink': shrink,
    'create-flow_tooltip-container__max-content': maxContent,
  });

  const expandedStyles = {
    height: expanded ? expandedImgRef?.current?.clientHeight : '0',
  };

  const expandable = !!expandedImgSrc;

  // If tooltip id is found in the hidden tooltips array, hide tooltip, render children only
  const hiddenTooltips = JSON.parse(localStorage.getItem('hidden-tooltips') || '[]');
  if (showOnce && hiddenTooltips.includes(id)) {
    return children;
  }

  return (
    <div className="create-flow_tooltip-wrapper">
      {children}
      {show && (
        <div
          data-qa-id={dataQaId}
          role="button"
          tabIndex="0"
          onClick={!expandable ? onClickCloseHandler : undefined}
          className={classNames}
          onKeyDown={(e) => {
            if (!expandable && (e.key === 'Enter' || e.key === ' ')) {
              onClickCloseHandler();
            }
          }}
        >
          <div className="create-flow_tooltip-container__content">
            <div
              className={cx('create-flow_tooltip-container__text', {
                'create-flow_tooltip-container__text_centered': centerText,
                'create-flow_tooltip-container__text_small': mobileTextSize,
              })}
            >
              <p>{text}</p>
              {expandable && !expanded && (
                <Button
                  data-qa-id="tooltip-expand-btn"
                  variant="unstyled-button"
                  onClick={() => setExpanded(true)}
                >
                  <InformationLineIcon ratio={0.75} />
                </Button>
              )}
            </div>
            {hasCloseButton && (
              <Button
                data-qa-id="close-tooltip-btn"
                variant="unstyled-button"
                onClick={onClickCloseHandler}
              >
                <CloseLineIcon ratio={0.5} />
              </Button>
            )}
          </div>
          <div className="create-flow_tooltip-container__expanded-content" style={expandedStyles}>
            {expandable && (
              <img
                alt="expanded"
                ref={expandedImgRef}
                className="create-flow_tooltip-container__expanded-image"
                src={expandedImgSrc}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node,
  text: PropTypes.string,
  className: PropTypes.string,
  position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  color: PropTypes.oneOf(['cotton_candy', 'tanzanite']),
  visible: PropTypes.bool,
  expandedImgSrc: PropTypes.string,
  dataQaId: PropTypes.string,
  hasCloseButton: PropTypes.bool,
  shimmer: PropTypes.bool,
  showOnce: PropTypes.bool,
  id: PropTypes.string,
};
